import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal, DataTable } from '../../../components/admin/shared';
import { Plus, Edit2, Trash2, Save, X, CheckCircle } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const StatusesConfig = () => {
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [selectedService, setSelectedService] = useState('overseas');
  const [editingStatus, setEditingStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [success, setSuccess] = useState(false);

  const services = [
    { value: 'overseas', label: 'Overseas Education' },
    { value: 'career', label: 'Career Counseling' },
    { value: 'skills', label: 'Skill Development' },
    { value: 'test-prep', label: 'Test Preparation' },
    { value: 'scholarships', label: 'Scholarship Finder' },
    { value: 'visa', label: 'Visa Assistance' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'pre-departure', label: 'Pre-Departure' },
    { value: 'post-arrival', label: 'Post-Arrival' },
    { value: 'alumni', label: 'Alumni Network' }
  ];

  const colorOptions = [
    { value: 'gray', label: 'Gray', class: 'bg-gray-100 text-gray-800' },
    { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-800' },
    { value: 'green', label: 'Green', class: 'bg-green-100 text-green-800' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-100 text-yellow-800' },
    { value: 'red', label: 'Red', class: 'bg-red-100 text-red-800' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-800' },
    { value: 'teal', label: 'Teal', class: 'bg-teal-100 text-teal-800' }
  ];

  useEffect(() => {
    loadStatuses();
  }, [selectedService]);

  const loadStatuses = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get(`/api/admin/settings/statuses?service=${selectedService}`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Error loading statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingStatus({
      id: null,
      service: selectedService,
      status: '',
      label: '',
      color: 'blue',
      icon: 'Circle',
      order: statuses.length + 1,
      emailNotify: false,
      active: true
    });
  };

  const handleEdit = (status) => {
    setEditingStatus({ ...status });
  };

  const handleSave = async () => {
    try {
      if (editingStatus.id) {
        // Update existing
        await educentialApi.patch(`/api/admin/settings/statuses/${editingStatus.id}`, editingStatus);
      } else {
        // Create new
        await educentialApi.post('/api/admin/settings/statuses', editingStatus);
      }

      setEditingStatus(null);
      setSuccess(true);
      loadStatuses();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving status:', error);
      alert('Failed to save status. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await educentialApi.delete(`/api/admin/settings/statuses/${showDeleteModal.id}`);
      setShowDeleteModal(null);
      setSuccess(true);
      loadStatuses();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting status:', error);
      alert('Failed to delete status. Please try again.');
    }
  };

  const getColorClass = (color) => {
    return colorOptions.find(c => c.value === color)?.class || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      key: 'order',
      label: 'Order',
      render: (status) => <span className="text-sm font-mono">{status.order}</span>
    },
    {
      key: 'status',
      label: 'Status Code',
      render: (status) => <span className="font-mono text-sm">{status.status}</span>
    },
    {
      key: 'label',
      label: 'Label',
      render: (status) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getColorClass(status.color)}`}>
          {status.label}
        </span>
      )
    },
    {
      key: 'emailNotify',
      label: 'Email Notify',
      render: (status) => (
        <span className={`text-sm ${status.emailNotify ? 'text-green-600' : 'text-gray-400'}`}>
          {status.emailNotify ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'active',
      label: 'Active',
      render: (status) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          status.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {status.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (status) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(status);
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(status);
            }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (loading && !editingStatus) {
    return (
      <AdminLayout title="Application Statuses">
        <LoadingState message="Loading application statuses..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Application Statuses">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Application Statuses</h2>
            <p className="text-gray-600 mt-1">Manage application status configurations for each service</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {services.map(service => (
                <option key={service.value} value={service.value}>
                  {service.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddNew}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Status</span>
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-900 font-medium">Status configuration updated successfully!</p>
          </div>
        )}

        {/* Edit Form */}
        {editingStatus && (
          <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingStatus.id ? 'Edit Status' : 'Add New Status'}
              </h3>
              <button
                onClick={() => setEditingStatus(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingStatus.status}
                  onChange={(e) => setEditingStatus({ ...editingStatus, status: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono"
                  placeholder="UNDER_REVIEW"
                />
                <p className="text-xs text-gray-500 mt-1">Use UPPER_CASE with underscores</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingStatus.label}
                  onChange={(e) => setEditingStatus({ ...editingStatus, label: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Under Review"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <select
                  value={editingStatus.color}
                  onChange={(e) => setEditingStatus({ ...editingStatus, color: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                >
                  {colorOptions.map(color => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getColorClass(editingStatus.color)}`}>
                    Preview
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                <input
                  type="number"
                  value={editingStatus.order}
                  onChange={(e) => setEditingStatus({ ...editingStatus, order: parseInt(e.target.value) || 1 })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  min="1"
                />
              </div>

              <div className="md:col-span-2 space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingStatus.emailNotify}
                    onChange={(e) => setEditingStatus({ ...editingStatus, emailNotify: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Send email notification when status changes to this</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingStatus.active}
                    onChange={(e) => setEditingStatus({ ...editingStatus, active: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active (visible in application forms)</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                onClick={() => setEditingStatus(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!editingStatus.status || !editingStatus.label}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>Save Status</span>
              </button>
            </div>
          </div>
        )}

        {/* Statuses Table */}
        <DataTable
          columns={columns}
          data={statuses}
          loading={loading}
          emptyMessage={`No statuses configured for ${services.find(s => s.value === selectedService)?.label}. Click "Add Status" to create one.`}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal !== null}
          title="Delete Status?"
          message={`Are you sure you want to delete the status "${showDeleteModal?.label}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          dangerous={true}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(null)}
        />
      </div>
    </AdminLayout>
  );
};

export default StatusesConfig;
